import React, { PropsWithChildren } from 'react';
import styled from '@emotion/styled';
import MuiAddRounded from '@mui/icons-material/AddRounded';
import MuiDoneRounded from '@mui/icons-material/DoneRounded';
import Box from '@mui/material/Box';
import CircularProgress from '@mui/material/CircularProgress';

import { useAddRemoveFavorite } from '~/src/common/favorites/useAddRemoveFavorite';
import { followButtonCircularProgressZIndex, pinkishOrange, turquoise } from '~/styles';

import { Button } from './buttons';

const AddIcon = styled(MuiAddRounded)`
  color: #fff;
  font-size: inherit;
` as React.FC<PropsWithChildren<React.ComponentProps<typeof MuiAddRounded>>>;

const DoneIcon = styled(MuiDoneRounded)`
  color: #fff;
  font-size: inherit;
` as React.FC<PropsWithChildren<React.ComponentProps<typeof MuiDoneRounded>>>;

interface Props {
  contentId: string;
  isLoadingExternally?: boolean;
}

export const FollowButton: React.FC<PropsWithChildren<Props>> = ({
  contentId,
  isLoadingExternally,
}) => {
  const {
    addFavorite,
    removeFavorite,
    loading: isAddRemoveLoading,
    isFavorite,
  } = useAddRemoveFavorite({
    contentId,
    isTeacher: true,
  });

  const loading = isAddRemoveLoading || isLoadingExternally;

  return (
    <Box
      bgcolor={isFavorite ? turquoise : pinkishOrange}
      borderRadius="50%"
      display="inline-flex"
      position="relative"
    >
      <Button
        style={{ alignItems: 'center' }}
        onClick={(e: React.MouseEvent<HTMLButtonElement, MouseEvent>) => {
          e.preventDefault();
          if (loading) return;
          if (isFavorite) removeFavorite();
          else addFavorite();
        }}
        aria-label="Toggle follow"
        kind="round"
        data-testid="article-teacher-follow-button"
        p={0}
      >
        {isFavorite ? <DoneIcon data-testid="is-following-teacher" /> : <AddIcon />}
      </Button>
      {loading && (
        <CircularProgress
          size={50}
          style={{
            zIndex: followButtonCircularProgressZIndex,
            color: isFavorite ? pinkishOrange : turquoise,
            position: 'absolute',
            top: '-5px',
            right: '-5px',
          }}
        />
      )}
    </Box>
  );
};
